// apps/web/src/offline/OfflineSyncService.js

import fetch from 'auth/FetchInterceptor'

import OfflineLeadService from './OfflineLeadService'
import OfflineSpinService from './OfflineSpinService'
import OfflineQuizAttemptService from './OfflineQuizAttemptService'

let syncing = false

/*
 * ============================================================
 * LEADS
 * ============================================================
 */

const syncPendingLeads = async () => {
  const pendingLeads =
    await OfflineLeadService
      .getPendingLeads()

  let synced = 0
  let failed = 0

  if (!pendingLeads.length) {
    return {
      synced,
      failed,
    }
  }

  console.log(
    '[SYNC] Leads pendentes:',
    pendingLeads.length,
  )

  for (const lead of pendingLeads) {
    if (!navigator.onLine) {
      break
    }

    try {
      const result =
        await fetch({
          url:
            '/public/leads',

          method:
            'post',

          silentNetworkError:
            true,

          data: {
            formId:
              lead.formId,

            eventId:
              lead.eventId,

            gameId:
              lead.gameId,

            values:
              lead.values,

            consentLgpd:
              lead.consentLgpd,
          },
        })

      const serverLeadId =
        result?.leadId

      if (!serverLeadId) {
        throw new Error(
          'SERVER_LEAD_ID_NOT_RETURNED',
        )
      }

      await OfflineLeadService
        .markLeadSynced(
          lead,
          String(
            serverLeadId,
          ),
        )

      synced += 1

      console.log(
        '[SYNC] Lead sincronizado:',
        lead.localId,
        '→',
        serverLeadId,
      )
    } catch (error) {
      failed += 1

      await OfflineLeadService
        .markLeadPending(
          lead,
          error,
        )

      console.warn(
        '[SYNC] Falha ao sincronizar lead:',
        lead.localId,
        error,
      )

      if (!navigator.onLine) {
        break
      }
    }
  }

  return {
    synced,
    failed,
  }
}

/*
 * ============================================================
 * RESOLVE LEAD LOCAL PARA ID DO SERVIDOR
 * ============================================================
 */

const resolveServerLeadId =
  async (leadId) => {
    if (!leadId) {
      return null
    }

    if (
      !String(
        leadId,
      ).startsWith('lead_')
    ) {
      return String(
        leadId,
      )
    }

    const localLead =
      await OfflineLeadService
        .getLead(
          leadId,
        )

    if (!localLead) {
      console.warn(
        '[SYNC] Lead local não encontrado:',
        leadId,
      )

      return null
    }

    if (
      localLead.serverLeadId
    ) {
      return String(
        localLead.serverLeadId,
      )
    }

    return null
  }

/*
 * ============================================================
 * SPINS
 * ============================================================
 */

const syncPendingSpins = async () => {
  const pendingSpins =
    await OfflineSpinService
      .getPendingSpins()

  let synced = 0
  let failed = 0
  let waitingLead = 0

  if (!pendingSpins.length) {
    return {
      synced,
      failed,
      waitingLead,
    }
  }

  console.log(
    '[SYNC] Giros pendentes:',
    pendingSpins.length,
  )

  const orderedSpins = [
    ...pendingSpins,
  ].sort(
    (a, b) =>
      new Date(
        a.createdAt || 0,
      ).getTime() -
      new Date(
        b.createdAt || 0,
      ).getTime(),
  )

  for (
    const spin
    of orderedSpins
  ) {
    if (!navigator.onLine) {
      break
    }

    try {
      const serverLeadId =
        await resolveServerLeadId(
          spin.leadId,
        )

      if (
        spin.leadId &&
        String(
          spin.leadId,
        ).startsWith(
          'lead_',
        ) &&
        !serverLeadId
      ) {
        waitingLead += 1

        console.warn(
          '[SYNC] Giro aguardando sincronização do lead:',
          spin.localId,
          spin.leadId,
        )

        continue
      }

      const result =
        await fetch({
          url:
            '/spins/offline-sync',

          method:
            'post',

          silentNetworkError:
            true,

          data: {
            clientOperationId:
              spin.clientOperationId,

            gameId:
              spin.gameId,

            prizeId:
              spin.prizeId,

            leadId:
              serverLeadId,

            playedAt:
              spin.createdAt,

            participant:
              spin.participant ||
              null,
          },
        })

      const serverSpinId =
        result?.id

      if (!serverSpinId) {
        throw new Error(
          'SERVER_SPIN_ID_NOT_RETURNED',
        )
      }

      await OfflineSpinService
        .markSpinSynced(
          spin,
          String(
            serverSpinId,
          ),
        )

      synced += 1

      console.log(
        '[SYNC] Giro sincronizado:',
        spin.localId,
        '→',
        serverSpinId,
      )

      if (
        result?.stockDivergence ===
        true
      ) {
        console.warn(
          '[SYNC] Giro sincronizado com divergência de estoque:',
          spin.localId,
        )
      }
    } catch (error) {
      failed += 1

      console.warn(
        '[SYNC] Falha ao sincronizar giro:',
        spin.localId,
        error,
      )

      const status =
        error?.response?.status

      if (
        status &&
        status >= 400 &&
        status < 500
      ) {
        await OfflineSpinService
          .markSpinError(
            spin,
            error?.response?.data
              ?.message ||
            error,
          )
      } else {
        await OfflineSpinService
          .markSpinPending(
            spin,
            error,
          )
      }

      if (!navigator.onLine) {
        break
      }
    }
  }

  return {
    synced,
    failed,
    waitingLead,
  }
}

/*
 * ============================================================
 * QUIZ
 * ============================================================
 */

const syncPendingQuizAttempts =
  async () => {
    const pendingAttempts =
      await OfflineQuizAttemptService
        .getPendingAttempts()

    let synced = 0
    let failed = 0
    let waitingLead = 0

    if (!pendingAttempts.length) {
      return {
        synced,
        failed,
        waitingLead,
      }
    }

    console.log(
      '[SYNC QUIZ] Tentativas pendentes:',
      pendingAttempts.length,
    )

    const orderedAttempts = [
      ...pendingAttempts,
    ].sort(
      (a, b) =>
        new Date(
          a.createdAt || 0,
        ).getTime() -
        new Date(
          b.createdAt || 0,
        ).getTime(),
    )

    for (
      const attempt
      of orderedAttempts
    ) {
      if (!navigator.onLine) {
        break
      }

      try {
        if (
          !attempt?.gameId ||
          !attempt?.localId
        ) {
          throw new Error(
            'QUIZ_OFFLINE_INVALID_ATTEMPT',
          )
        }

        /*
         * ==========================================
         * RESOLVE LEAD
         * ==========================================
         */

        const serverLeadId =
          await resolveServerLeadId(
            attempt.leadId,
          )

        if (
          attempt.leadId &&
          String(
            attempt.leadId,
          ).startsWith(
            'lead_',
          ) &&
          !serverLeadId
        ) {
          waitingLead += 1

          console.warn(
            '[SYNC QUIZ] Tentativa aguardando lead:',
            attempt.localId,
            attempt.leadId,
          )

          continue
        }

        /*
         * ==========================================
         * ENVIA TENTATIVA COMPLETA
         * ==========================================
         */

        const result =
          await fetch({
            url:
              `/quiz-games/${attempt.gameId}/offline-sync`,

            method:
              'post',

            silentNetworkError:
              true,

            data: {
              clientOperationId:
                attempt.localId,

              serverAttemptId:
                attempt.serverAttemptId ||
                null,

              leadId:
                serverLeadId,

              participant:
                attempt.participant ||
                null,

              answers:
                Array.isArray(
                  attempt.answers,
                )
                  ? attempt.answers
                  : [],

              startedAt:
                attempt.startedAt ||
                attempt.createdAt ||
                null,

              finishedAt:
                attempt.finishedAt ||
                null,
            },
          })

        if (!result?.id) {
          throw new Error(
            'QUIZ_SYNC_INVALID_RESPONSE',
          )
        }

        /*
         * Somente removemos a tentativa local
         * depois da confirmação da API.
         */

        await OfflineQuizAttemptService
          .removeAttempt(
            attempt.localId,
          )

        synced += 1

        console.log(
          '[SYNC QUIZ] Tentativa sincronizada:',
          attempt.localId,
          '→',
          result.id,
        )
      } catch (error) {
        failed += 1

        console.warn(
          '[SYNC QUIZ] Falha ao sincronizar:',
          attempt?.localId,
          error,
        )

        /*
         * Não apagamos tentativas do Quiz em erro.
         *
         * Mesmo em 400/409, preservamos localmente
         * para evitar perda de participação.
         */

        if (!navigator.onLine) {
          break
        }
      }
    }

    return {
      synced,
      failed,
      waitingLead,
    }
  }

/*
 * ============================================================
 * SINCRONIZAÇÃO COMPLETA
 * ============================================================
 */

const syncAll = async () => {
  if (syncing) {
    console.log(
      '[SYNC] Já existe sincronização em andamento.',
    )

    return {
      skipped:
        true,
    }
  }

  if (!navigator.onLine) {
    return {
      skipped:
        true,

      reason:
        'OFFLINE',
    }
  }

  syncing = true

  console.log(
    '[SYNC] Iniciando sincronização...',
  )

  try {
    /*
     * ORDEM:
     *
     * 1. Leads
     * 2. Spins
     * 3. Quiz
     *
     * O Quiz vem depois dos Leads porque
     * uma tentativa offline pode depender
     * de um lead criado offline.
     */

    const leads =
      await syncPendingLeads()

    const spins =
      await syncPendingSpins()

    const quiz =
      await syncPendingQuizAttempts()

    const result = {
      skipped:
        false,

      leads,

      spins,

      quiz,

      finishedAt:
        new Date()
          .toISOString(),
    }

    console.log(
      '[SYNC] Sincronização finalizada:',
      result,
    )

    return result
  } finally {
    syncing = false
  }
}

/*
 * ============================================================
 * EXPORT
 * ============================================================
 */

const OfflineSyncService = {
  syncPendingLeads,
  syncPendingSpins,
  syncPendingQuizAttempts,
  syncAll,
}

export default OfflineSyncService